import { ReactNode, useCallback } from 'react';
import { List } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import { SlackChannelDeepLink } from 'components/SlackChannelDeepLink';
import {
  getSlackChannelsFromMetadata,
  useLatLngMetadata,
} from 'hooks/useLatLngMetadata';
import { ExternalGeoEventField } from 'pages/ExternalGeoEventDetails/ExternalGeoEventDetails.types';
import {
  getFields,
  showFireInfoForEvent,
  showFloodingInfoForEvent,
} from 'pages/ExternalGeoEventDetails/ExternalGeoEventDetails.utils';
import { ExternalGeoEvent } from 'shared/types';
import { ExternalGeoNameMap } from '../../../constants';
import { ExternalGeoEventListItem } from './ExternalGeoEventListItem';

type ExternalGeoEventInfoProps = {
  event: ExternalGeoEvent;
};

const renderer = (
  event: ExternalGeoEvent,
  field: ExternalGeoEventField,
  t: TFunction,
): string | ReactNode => {
  if (field.renderer) {
    return field.renderer(event, t);
  }
  const data = event.data as unknown as Record<
    string,
    string | number | undefined
  >;
  const value = data[field.key];
  return value != null && value !== '' && value !== 'none'
    ? String(value)
    : t('externalGeoEvent.unknown');
};

export const ExternalGeoEventFields = (
  props: ExternalGeoEventInfoProps,
): JSX.Element => {
  const { event } = props;
  const { t } = useTranslation();
  const { latLngMetadata } = useLatLngMetadata({
    enabled: true,
    lat: event.lat,
    lng: event.lng,
  });

  const onSourceClick = useCallback(() => {
    if (event.data.sourceUrl) {
      window.open(event.data.sourceUrl, '_blank', 'noopener,noreferrer');
    }
  }, [event.data.sourceUrl]);

  const fields = getFields(event);

  const slackChannels = getSlackChannelsFromMetadata(latLngMetadata);

  return (
    <List>
      <ExternalGeoEventListItem
        icon={event.data.sourceUrl ? <OpenInNew /> : undefined}
        onClick={event.data.sourceUrl ? onSourceClick : undefined}
        primary={t('externalGeoEvent.fields.source')}
        secondary={
          event.data.sourceLabel ?? ExternalGeoNameMap[event.externalSource]
        }
      />

      {latLngMetadata && (
        <>
          {showFireInfoForEvent(event) && (
            <SlackChannelDeepLink
              label={t('externalGeoEvent.slackChannel.wildfire')}
              channelId={slackChannels.wildfireChannelId}
              channelName={slackChannels.wildfireChannel}
            />
          )}
          {showFloodingInfoForEvent(event) && (
            <SlackChannelDeepLink
              label={t('externalGeoEvent.slackChannel.flooding')}
              channelId={slackChannels.floodingChannelId}
              channelName={slackChannels.floodingChannel}
            />
          )}
        </>
      )}

      {fields.map((field) => {
        return (
          <ExternalGeoEventListItem
            key={field.label}
            icon={field.onClick ? <OpenInNew /> : undefined}
            onClick={field.onClick}
            primary={t(field.label)}
            secondary={renderer(event, field, t)}
          />
        );
      })}
    </List>
  );
};
